export type CampaignSourceKind = 'avito' | 'hh' | 'other';

export const DEFAULT_MESSENGER_PRIORITY = ['AVITO', 'MAX', 'TELEGRAM', 'WHATSAPP', 'HH'];

const SUPPORTED_MESSENGERS = new Set(DEFAULT_MESSENGER_PRIORITY);

export const parseMaybeObject = (value: unknown): Record<string, any> | null => {
	if (!value) return null;
	if (typeof value === 'string') {
		try {
			const parsed = JSON.parse(value);
			return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
				? parsed as Record<string, any>
				: null;
		} catch {
			return null;
		}
	}
	if (typeof value === 'object' && !Array.isArray(value)) {
		return value as Record<string, any>;
	}
	return null;
};

export const normalizeMessengerCode = (value: unknown): string => String(value ?? '').trim().toUpperCase();

const normalizeSourceCode = (value: unknown): string => String(value ?? '').trim().toLowerCase();

const hasMessenger = (value: unknown, messenger: string): boolean =>
	Array.isArray(value) && value.some((item) => normalizeMessengerCode(item) === messenger);

export const getCampaignSourceKind = (
	campaign: unknown,
	searchFiltersValue?: unknown,
	accountSettingsValue?: unknown
): CampaignSourceKind => {
	const campaignData = parseMaybeObject(campaign) || {};
	const searchFilters = searchFiltersValue === undefined
		? parseMaybeObject(campaignData.searchFilters)
		: parseMaybeObject(searchFiltersValue);
	const accountSettings = accountSettingsValue === undefined
		? parseMaybeObject(campaignData.accountSettings)
		: parseMaybeObject(accountSettingsValue);

	const candidateSource = normalizeMessengerCode(campaignData.candidateSource);
	const searchSource = normalizeSourceCode(searchFilters?.source);
	const hasAvitoVacancy = Boolean(campaignData.avitoVacancyId);
	const hasHhVacancy = Boolean(campaignData.hhVacancyId);
	const enabledHasAvito = hasMessenger(accountSettings?.enabledMessengers, 'AVITO');
	const enabledHasHh = hasMessenger(accountSettings?.enabledMessengers, 'HH');
	const priorityHasAvito = hasMessenger(accountSettings?.messengerPriority, 'AVITO');
	const priorityHasHh = hasMessenger(accountSettings?.messengerPriority, 'HH');

	if (
		candidateSource === 'AVITO' ||
		searchSource === 'avito' ||
		hasAvitoVacancy ||
		((enabledHasAvito || priorityHasAvito) && !enabledHasHh && !hasHhVacancy)
	) {
		return 'avito';
	}

	if (
		candidateSource === 'HH' ||
		candidateSource === 'HEADHUNTER' ||
		searchSource === 'hh' ||
		hasHhVacancy ||
		((enabledHasHh || priorityHasHh) && !enabledHasAvito)
	) {
		return 'hh';
	}

	return 'other';
};

export const getAllowedMessengersForSource = (source: CampaignSourceKind): string[] => {
	if (source === 'avito') {
		return DEFAULT_MESSENGER_PRIORITY.filter((messenger) => messenger !== 'HH');
	}
	if (source === 'hh') {
		return DEFAULT_MESSENGER_PRIORITY.filter((messenger) => messenger !== 'AVITO');
	}
	return [...DEFAULT_MESSENGER_PRIORITY];
};

export const normalizeMessengerPriorityForSource = (
	savedPriority: unknown,
	source: CampaignSourceKind = 'other'
): string[] => {
	const allowed = new Set(getAllowedMessengersForSource(source));
	const saved = Array.isArray(savedPriority)
		? Array.from(new Set(savedPriority
			.map((messenger) => normalizeMessengerCode(messenger))
			.filter((messenger) => SUPPORTED_MESSENGERS.has(messenger) && allowed.has(messenger))))
		: [];

	return [
		...saved,
		...DEFAULT_MESSENGER_PRIORITY.filter((messenger) => allowed.has(messenger) && !saved.includes(messenger))
	];
};
